<template>
  <div class="personal-info-container">
    <!-- 标题 -->
<!--    <h1 class="title">Upload Your Photo</h1>-->
    
    <!-- 主要内容区域 -->
    <div class="main-content">
      <div class="form-content">
        <!-- 摄像头拍照区域 -->
        <div v-if="!uploadedImage" class="camera-container">
          <div class="camera-preview" ref="cameraPreview">
            <video ref="videoElement" autoplay muted playsinline class="camera-video"></video>
            <canvas ref="canvasElement" class="hidden-canvas"></canvas>
            <!-- 倒计时显示 -->
            <div v-if="countdown > 0" class="countdown-overlay">
              <div class="countdown-circle">
                <div class="countdown-number">{{ countdown }}</div>
              </div>
            </div>
            <!-- Take Photo按钮 -->
            <div v-if="isCameraMode && countdown === 0 && !isCameraLoading" class="take-photo-overlay">
              <div class="photo-instructions">
                <div class="instruction-text">准备好了吗？</div>
                <div class="instruction-text">请露出面部，整理好衣着，</div>
                <div class="instruction-text">点击"拍照"按钮后</div>
                <div class="instruction-text">贴纸处站到地面</div>
                <div class="instruction-text">镜头将在 <span class="countdown-highlight">3</span> 秒倒计时后自动拍摄</div>
              </div>
              <button class="take-photo-btn" @click="startCountdown">
                <div class="take-photo-icon"><img :src="camera"/></div>
                <div class="take-photo-text">拍摄照片</div>
              </button>
            </div>
            <!-- 摄像头加载中 -->
            <div v-if="isCameraLoading" class="take-photo-overlay">
              <div class="loading-content">
                <div class="loading-spinner"></div>
                <div class="loading-text">正在启动摄像头...</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 拍照结果显示区域 -->
        <div v-if="uploadedImage" class="photo-result-container">
          <div class="photo-result">
            <img :src="uploadedImage" alt="拍摄的照片" class="captured-photo" />
            <div class="photo-success-text">拍照成功！正在处理...</div>
          </div>
        </div>

<!--        &lt;!&ndash; 下一步按钮 &ndash;&gt;
        <button class="cta" @click="goToNextStep" :disabled="!isFormValid || isLoading">
          Next
        </button>-->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import camera from '@/assets/images/svg/camera.svg'
import { StartExploreApi } from '@/api/startExplore'
import { expiredStorage } from '@/utils/expiredStorage'


// 定义 emits
const emit = defineEmits(['next', 'validation-change', 'data-change'])

// 定义 props 来接收前面步骤的数据
const props = defineProps({
  step1Data: {
    type: Object,
    default: () => ({})
  },
  step2Data: {
    type: Object,
    default: () => ({})
  },
  initialData: {
    type: Object,
    default: () => ({})
  }
})

// 响应式数据（支持回填）
const fileInput = ref(null)
const uploadedImage = ref(
  props.initialData.avatarBase64 || 
  props.initialData.avatarUrl || 
  null
)
const isLoading = ref(false)
const uploadResult = ref(null) // 存储API返回的JSON数据

// 摄像头相关状态
const isCameraMode = ref(false)
const isCameraReady = ref(false)
const isCameraLoading = ref(false)
const capturedImage = ref(null)
const videoElement = ref(null)
const canvasElement = ref(null)
const cameraPreview = ref(null)
const mediaStream = ref(null)
const countdown = ref(0)
const countdownTimer = ref(null)

// 计算属性 - 验证是否可以开始探索
const isFormValid = computed(() => {
  return uploadedImage.value !== null
})

// 监听验证状态变化并通知父组件
watch(isFormValid, (newValue) => {
  nextTick(() => {
    emit('validation-change', newValue)
  })
})

// 监听props变化，更新本地数据
watch(() => props.initialData, (newInitialData) => {
  if (newInitialData) {
    // 优先使用base64数据，如果没有则使用avatarUrl
    if (newInitialData.avatarBase64) {
      uploadedImage.value = newInitialData.avatarBase64
    } else if (newInitialData.avatarUrl) {
      uploadedImage.value = newInitialData.avatarUrl
    }
  }
}, { deep: true })

// 组件挂载后手动触发一次验证状态检查
onMounted(async () => {
  // 自动启动摄像头
  await selectCameraMode()
  
  nextTick(() => {
    emit('validation-change', isFormValid.value)
  })
})

// 选择摄像头模式
const selectCameraMode = async () => {
  try {
    isCameraLoading.value = true
    isCameraMode.value = true
    await startCamera()
    console.log('摄像头启动成功')
  } catch (error) {
    console.error('摄像头启动失败:', error)
    isCameraMode.value = false
    // 可以在这里添加用户提示
  } finally {
    isCameraLoading.value = false
  }
}

// 开始倒计时
const startCountdown = () => {
  if (countdown.value > 0) return
  
  // 开始3秒倒计时
  countdown.value = 3
  
  countdownTimer.value = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer.value)
      countdownTimer.value = null
      performCapture()
    }
  }, 1000)
}

// 检查摄像头权限
const checkCameraPermission = async () => {
  try {
    const permission = await navigator.permissions.query({ name: 'camera' })
    console.log('摄像头权限状态:', permission.state)
    return permission.state
  } catch (error) {
    console.log('无法检查权限状态:', error)
    return 'unknown'
  }
}

// 启动摄像头
const startCamera = async () => {
  try {
    // 检查权限状态
    const permissionState = await checkCameraPermission()
    if (permissionState === 'denied') {
      alert('摄像头权限被拒绝，请在浏览器设置中允许摄像头访问，然后刷新页面重试')
      isCameraMode.value = false
      return
    }
    
    // 先尝试4K分辨率
    let stream
    try {
      console.log('🎥 尝试启动4K摄像头...')
      stream = await navigator.mediaDevices.getUserMedia({
          audio:true,
        video: {
          facingMode: 'user', // 前置摄像头
            width: { ideal: 1920 },
            height: { ideal: 2000 }
        },
      })
      console.log('✅ 4K摄像头启动成功')
    } catch (error) {
      console.log('⚠️ 4K摄像头启动失败，尝试1080p...')
      // 如果4K失败，尝试1080p
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 15 }
        }
      })
      console.log('✅ 1080p摄像头启动成功')
    }
    
    mediaStream.value = stream
    if (videoElement.value) {
      videoElement.value.srcObject = stream
      videoElement.value.onloadedmetadata = () => {
        isCameraReady.value = true
        
        // 显示实际使用的摄像头分辨率
        const videoTrack = stream.getVideoTracks()[0]
        if (videoTrack) {
          const settings = videoTrack.getSettings()
          console.log('🎥 摄像头实际分辨率:', {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            aspectRatio: settings.aspectRatio
          })
          
          // 检查是否为4K
          if (settings.width >= 3840 && settings.height >= 2160) {
            console.log('✅ 正在使用4K摄像头!')
          } else if (settings.width >= 1920 && settings.height >= 1080) {
            console.log('✅ 正在使用1080p摄像头')
          } else {
            console.log('⚠️ 摄像头分辨率较低:', settings.width + 'x' + settings.height)
          }
        }
      }
    }
  } catch (error) {
    console.error('摄像头访问错误:', error)
    
    // 详细的错误处理
    let errorMessage = '无法访问摄像头'
    
    if (error.name === 'NotAllowedError') {
      errorMessage = '摄像头权限被拒绝，请在浏览器设置中允许摄像头访问'
    } else if (error.name === 'NotFoundError') {
      errorMessage = '未找到摄像头设备，请检查摄像头连接'
    } else if (error.name === 'NotReadableError') {
      errorMessage = '摄像头被其他应用占用，请关闭其他使用摄像头的应用'
    } else if (error.name === 'OverconstrainedError') {
      errorMessage = '摄像头不支持请求的分辨率，尝试使用较低分辨率'
      // 尝试降级到较低分辨率
      try {
        console.log('尝试使用较低分辨率...')
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        })
        mediaStream.value = fallbackStream
        if (videoElement.value) {
          videoElement.value.srcObject = fallbackStream
          videoElement.value.onloadedmetadata = () => {
            isCameraReady.value = true
            console.log('✅ 降级分辨率成功')
          }
        }
        return // 成功获取降级分辨率，直接返回
      } catch (fallbackError) {
        console.error('降级分辨率也失败:', fallbackError)
        errorMessage = '摄像头无法使用，请检查设备或浏览器设置'
      }
    } else if (error.name === 'SecurityError') {
      errorMessage = '安全错误：请确保在HTTPS环境下使用摄像头'
    } else if (error.name === 'TypeError') {
      errorMessage = '浏览器不支持摄像头功能'
    }
    
    console.error('摄像头错误详情:', {
      name: error.name,
      message: error.message,
      constraint: error.constraint
    })
    
    alert(errorMessage)
    isCameraMode.value = false
  }
}


// 执行实际拍照
const performCapture = () => {
  if (!videoElement.value || !canvasElement.value) return
  
  const video = videoElement.value
  const canvas = canvasElement.value
  const context = canvas.getContext('2d')
  
  // 设置canvas尺寸与video相同（保持4K分辨率）
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  
  console.log('📸 拍照分辨率:', {
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height
  })
  
  // 绘制当前帧到canvas（保持高质量）
  context.drawImage(video, 0, 0, canvas.width, canvas.height)
  
  // 转换为base64（高质量，适合4K）
  const base64Image = canvas.toDataURL('image/jpeg', 0.95)
  
  // 直接使用拍照结果
  uploadedImage.value = base64Image
  
  // 将base64数据转换为File对象
  const byteCharacters = atob(base64Image.split(',')[1])
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const file = new File([byteArray], 'camera-photo.jpg', { type: 'image/jpeg' })
  
  // 触发数据变化事件
  emit('data-change', {
    avatarUrl: uploadedImage.value,
    avatarBase64: base64Image,
    avatarFile: file
  })
  
  // 调用API上传图片
  uploadPhotoAndProceed(file, base64Image)
}

// 上传图片并处理结果
const uploadPhotoAndProceed = (file, base64Image) => {
  // 立即显示提示，1秒后跳转到下一步（不等待API完成）
  setTimeout(() => {
    console.log('拍照完成，1秒后自动进入下一步')
    // 在跳转前清理摄像头
    stopCamera()
    isCameraMode.value = false
    capturedImage.value = null
    
    emit('next', {
      avatarUrl: uploadedImage.value,
      avatarBase64: base64Image,
      avatarFile: file,
      uploadResult: uploadResult.value // 传递可能已完成的API结果，或null
    })
  }, 1000)
  
  // 在后台异步执行API请求（不阻塞跳转）
  ;(async () => {
    try {
      console.log('后台开始上传图片到API...')
      console.log('文件信息:', {
        name: file.name,
        size: file.size,
        type: file.type
      })
      
      // 构建身体描述（从前面步骤的数据中获取）
      const bodyDescription = `${props.step2Data.height || 170}cm, ${props.step2Data.weight || 60}kg`
      console.log('身体描述:', bodyDescription)
      console.log('Step2数据:', props.step2Data)
      
      // 调用API上传图片
      const result = await StartExploreApi.analyzeOutfit({
        face_image: file,
        // body_description: bodyDescription // 添加body_description参数
      })
      // console.log('后台API返回数据内容:', JSON.stringify(result, null, 2))
      
      // 保存API返回的JSON数据
      uploadResult.value = result
      
      // 保存数据到localStorage
      try {
        const dataToStore = {
          uploadResult: result
        }
        expiredStorage.setItem('agent-form-step44', dataToStore)
        console.log('拍照API数据已保存到localStorage:', dataToStore)
      } catch (e) {
        console.warn('保存拍照数据到localStorage失败:', e)
      }
      
      // 将结果传递给父组件
      const dataToEmit = {
        avatarUrl: uploadedImage.value,
        avatarBase64: base64Image,
        avatarFile: file,
        uploadResult: result
      }
      console.log('后台发送data-change事件，数据:', dataToEmit)
      emit('data-change', dataToEmit)
      
    } catch (error) {
      console.error('后台API上传失败:', error)
      
      // API失败，保存数据到localStorage
      try {
        const dataToStore = {
          uploadResult: null
        }
        expiredStorage.setItem('agent-form-step44', dataToStore)
        console.log('拍照失败数据已保存到localStorage:', dataToStore)
      } catch (e) {
        console.warn('保存拍照失败数据到localStorage失败:', e)
      }
    }
  })()
}

// 停止摄像头
const stopCamera = () => {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop())
    mediaStream.value = null
  }
  isCameraReady.value = false
}

// 进入下一步（不提交API）
const goToNextStep = () => {
  if (!isFormValid.value) return
  
  // 只保存数据，不调用API
  const stepData = {
    avatarUrl: uploadedImage.value
  }
  
  // 如果当前FormContainer中有base64数据，也要传递
  if (props.initialData.avatarBase64) {
    stepData.avatarBase64 = props.initialData.avatarBase64
  }
  
  // 如果有File对象，也要传递
  if (props.initialData.avatarFile) {
    stepData.avatarFile = props.initialData.avatarFile
  }
  
  emit('next', stepData)
}

// 暴露方法给父组件调用
defineExpose({
  handleSubmit: goToNextStep
})

// 清理资源
onBeforeUnmount(() => {
  if (uploadedImage.value) {
    URL.revokeObjectURL(uploadedImage.value)
  }
  // 清理摄像头资源
  stopCamera()
  // 清理倒计时
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value)
    countdownTimer.value = null
  }
})
</script>

<style scoped>
/* 主容器 */
.personal-info-container {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
}

/* 标题样式 */
.title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 32px;
  font-weight: 400;
  margin-bottom: 40px;
  letter-spacing: 0.8px;
  line-height: 1.2;
  text-align: center;
}

/* 主要内容区域 */
.main-content {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 350px;
}

/* 表单内容区域 */
.form-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 400px;
  margin: 0 auto;
  align-items: center;
}

/* 图片上传区域 */
.upload-box { 
  width: 200px; 
  height: 200px; 
  border-radius: 12px; 
  background: rgba(0,0,0,.25); 
  border: 2px solid rgba(255,255,255,.15); 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: center; 
  gap: 10px; 
  cursor: pointer; 
  position: relative; 
  overflow: hidden;
  transition: all 0.3s ease;
}

.upload-box:hover {
  border-color: rgba(76, 175, 80, 0.5);
  background: rgba(0,0,0,.35);
}

.upload-bg { 
  position: absolute; 
  left: 0; 
  top: 0; 
  width: 100%; 
  height: 100%; 
  background: rgba(0,0,0,.3); 
  opacity: 0; 
  transition: opacity .25s ease; 
  z-index: 1; 
}

.upload-box:hover .upload-bg { 
  opacity: 1; 
}

.upload-figure { 
  width: 60px; 
  height: 60px; 
  position: relative; 
  z-index: 2;
  opacity: 0.8;
}

.upload-overlay { 
  position: absolute; 
  left: 50%; 
  top: 50%; 
  transform: translate(-50%, -50%); 
  width: 60px; 
  height: 60px; 
  border-radius: 8px; 
  display: none; 
  opacity: 0.9; 
  z-index: 3; 
  object-fit: contain; 
}

.upload-box:hover .upload-overlay { 
  display: block; 
  opacity: 1; 
}

.upload-box:hover .upload-text {
  color: rgba(76, 175, 80, 1);
}

.upload-full { 
  width: 100%; 
  height: 100%; 
  object-fit: cover; 
  display: block;
  border-radius: 10px;
}

.upload-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  opacity: .85;
  position: relative;
  z-index: 2;
}

.hidden-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

/* 加载状态样式 */
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid rgba(166, 245, 93, 1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 竖屏时优化加载动画 */
@media (orientation: portrait) {
  .loading-content {
    gap: 25px !important;
    padding: 20px !important;
  }
  
  .loading-spinner {
    width: 50px !important;
    height: 50px !important;
    border: 4px solid rgba(255, 255, 255, 0.3) !important;
    border-top: 4px solid rgba(166, 245, 93, 1) !important;
    animation: spin-mobile 1s linear infinite !important;
    -webkit-animation: spin-mobile 1s linear infinite !important;
    transform: translateZ(0); /* 启用硬件加速 */
    -webkit-transform: translateZ(0);
    will-change: transform; /* 提示浏览器优化 */
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
  
  .loading-text {
    font-size: 18px !important;
    font-weight: 600 !important;
    color: rgba(255, 255, 255, 1) !important;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5) !important;
    letter-spacing: 0.5px !important;
  }
  
  /* 确保加载容器在竖屏时可见 */
  .take-photo-overlay {
    background: linear-gradient(135deg, 
      rgba(0, 0, 0, 0.6) 0%, 
      rgba(20, 20, 30, 0.8) 50%,
      rgba(0, 0, 0, 0.6) 100%) !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }
}

/* 移动端专用旋转动画 */
@keyframes spin-mobile {
  0% { 
    transform: rotate(0deg) translateZ(0); 
    -webkit-transform: rotate(0deg) translateZ(0);
  }
  100% { 
    transform: rotate(360deg) translateZ(0); 
    -webkit-transform: rotate(360deg) translateZ(0);
  }
}

/* 拍照指导文字样式 */
.photo-instructions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  max-width: 700px;
}

.instruction-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 46px;
  font-weight: 1000;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.countdown-highlight {
  color: rgba(166, 245, 93, 1);
  font-weight: 700;
  font-size: 46px;
  text-shadow: 0 0 8px rgba(166, 245, 93, 0.5);
}

/* Take Photo按钮覆盖层样式 */
.take-photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.4) 0%, 
    rgba(20, 20, 30, 0.6) 50%,
    rgba(0, 0, 0, 0.4) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  border-radius: 16px;
  backdrop-filter: blur(8px);
}

.take-photo-btn {
    margin-top:10%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: rgba(255,255,255,0.4);
  color: rgba(255, 255, 255, 0.98);
  border: none;
  border-radius: 25px;
  font-size: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(15px);
  box-shadow:
    0 8px 25px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2);
  justify-content: center;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  height: 66px;
}

.take-photo-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
  box-shadow: 
    0 12px 35px rgba(0, 0, 0, 0.4),
    0 6px 16px rgba(0, 0, 0, 0.3);
}
.take-photo-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 1),
    rgba(178, 178, 178, 0.8),
    rgba(178, 178, 178, 0.4),
    rgba(178, 178, 178, 0.4),
    rgba(178, 178, 178, 0.8),
    rgba(255, 255, 255, 0.9));
    border-radius: 25px;
    padding: 2px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    z-index: -1;
}

.take-photo-icon {
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.take-photo-icon img {
  width: 27px;
  height: 34px;
  object-fit: contain;
}

.take-photo-text {
  font-size: 30px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* 已上传图片容器样式 */
.uploaded-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  height: 100%;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.retake-btn {
  padding: 12px 24px;
  background: rgba(166, 245, 93, 0.9);
  color: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.retake-btn:hover {
  background: rgba(166, 245, 93, 1);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

/* 摄像头容器样式 */
.camera-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 90vw;
  height: 97vh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, 
    rgba(20, 20, 30, 0.95) 0%, 
    rgba(30, 30, 45, 0.9) 25%,
    rgba(25, 25, 40, 0.95) 50%,
    rgba(35, 35, 50, 0.9) 75%,
    rgba(20, 20, 30, 0.95) 100%);
  backdrop-filter: blur(15px);
  z-index: 1000;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

/* 拍照结果显示区域 */
.photo-result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 90vw;
  height: 97vh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, 
    rgba(20, 20, 30, 0.95) 0%, 
    rgba(30, 30, 45, 0.9) 25%,
    rgba(25, 25, 40, 0.95) 50%,
    rgba(35, 35, 50, 0.9) 75%,
    rgba(20, 20, 30, 0.95) 100%);
  backdrop-filter: blur(15px);
  z-index: 1000;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.photo-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  width: 100%;
  height: 100%;
  padding: 40px;
}

.captured-photo {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.photo-success-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  padding: 16px 24px;
  background: rgba(76, 175, 80, 0.8);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  animation: successPulse 1s ease-in-out infinite;
}

@keyframes successPulse {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

.camera-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(100, 100, 120, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(80, 80, 100, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(120, 120, 140, 0.05) 0%, transparent 70%);
  pointer-events: none;
}

.camera-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.02) 50%, transparent 70%),
    linear-gradient(-45deg, transparent 30%, rgba(255, 255, 255, 0.01) 50%, transparent 70%);
  pointer-events: none;
}

.camera-preview {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, 
    rgba(15, 15, 25, 0.8) 0%, 
    rgba(25, 25, 35, 0.7) 50%,
    rgba(20, 20, 30, 0.8) 100%);
  border: none;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 4K视频渲染优化 */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: high-quality;
  /* 硬件加速 */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
  /* 4K视频质量优化 */
  -webkit-filter: contrast(1.1) brightness(1.05) saturate(1.1);
  filter: contrast(1.1) brightness(1.05) saturate(1.1);
  /* 抗锯齿 */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.hidden-canvas {
  display: none;
}

/* 倒计时覆盖层样式 */
.countdown-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  pointer-events: none;
}

.countdown-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  margin-bottom: 8px;
}

/* 倒计时圆形背景 */
.countdown-circle {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 0 40px rgba(0, 0, 0, 0.3),
    inset 0 0 20px rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
}

.countdown-number {
  color: rgba(166, 245, 93, 1);
  font-size: 100px;
  font-weight: 900;
  text-align: center;
  text-shadow: 0 0 30px rgba(166, 245, 93, 0.8);
  animation: countdownPulse 1s ease-in-out infinite;
  line-height: 1;
  font-family: 'Arial', sans-serif;
}

.countdown-instruction {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  text-align: center;
  max-width: 200px;
  line-height: 1.3;
  margin-top: 8px;
}

@keyframes countdownPulse {
  0% {
    transform: scale(1);
    opacity: 1;
    text-shadow: 0 0 30px rgba(166, 245, 93, 0.8);
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
    text-shadow: 0 0 40px rgba(166, 245, 93, 1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    text-shadow: 0 0 30px rgba(166, 245, 93, 0.8);
  }
}

.camera-controls {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 20px;
  width: auto;
  z-index: 1001;
}

.camera-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
}

.camera-btn:hover:not(:disabled) {
  border-color: rgba(76, 175, 80, 0.6);
  background: rgba(76, 175, 80, 0.1);
  transform: translateY(-1px);
}

.camera-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.capture-btn:hover:not(:disabled) {
  border-color: rgba(76, 175, 80, 0.8);
  background: rgba(76, 175, 80, 0.2);
}

.cancel-btn:hover:not(:disabled) {
  border-color: rgba(255, 99, 99, 0.6);
  background: rgba(255, 99, 99, 0.1);
}

.capture-icon, .cancel-icon {
  font-size: 20px;
}

.capture-text, .cancel-text {
  font-size: 12px;
  font-weight: 500;
}

/* 拍照预览样式 */
.capture-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 400px;
}

.preview-image {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.preview-controls {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.preview-btn {
  padding: 10px 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
}

.preview-btn:hover {
  transform: translateY(-1px);
}

.retake-btn:hover {
  border-color: rgba(255, 193, 7, 0.6);
  background: rgba(255, 193, 7, 0.1);
}

.confirm-btn:hover {
  border-color: rgba(76, 175, 80, 0.6);
  background: rgba(76, 175, 80, 0.1);
}

/* 开始探索按钮 */
.cta {
  margin-top: 20px;
  padding: 12px 36px;
  border-radius: 24px;
  background: rgba(76, 175, 80, 0.9);
  color: white;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.cta:hover:not(:disabled) {
  background: rgba(76, 175, 80, 1);
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.cta:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: rgba(100, 100, 100, 0.3);
}

.cta:active:not(:disabled) {
  transform: scale(0.95);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .personal-info-container {
    max-width: 600px;
    min-height: 400px;
  }
  
  .main-content {
    min-height: 300px;
  }
  
  .title {
    font-size: 26px;
    margin-bottom: 35px;
  }
  
  .form-content {
    gap: 25px;
    max-width: 350px;
  }
  
  .upload-box {
    width: 180px;
    height: 180px;
  }
  
  .upload-figure {
    width: 50px;
    height: 50px;
  }
  
  .upload-overlay {
    width: 50px;
    height: 50px;
  }
  
  .cta {
    padding: 10px 30px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .personal-info-container {
    min-height: 350px;
  }
  
  .main-content {
    min-height: 280px;
  }
  
  .title {
    font-size: 22px;
    margin-bottom: 30px;
  }
  
  .form-content {
    gap: 20px;
    max-width: 300px;
  }
  
  .upload-box {
    width: 160px;
    height: 160px;
  }
  
  .upload-figure {
    width: 45px;
    height: 45px;
  }
  
  .upload-overlay {
    width: 45px;
    height: 45px;
  }
  
  .upload-text {
    font-size: 13px;
  }
  
  .cta {
    padding: 8px 24px;
    font-size: 14px;
  }

  /* 移动端模式选择样式 */
  .mode-buttons {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .mode-btn {
    width: 100%;
    min-width: auto;
    padding: 14px 20px;
  }

  /* 移动端摄像头样式 */
  .camera-container {
    width: 90vw;
    height: 90vh;
    gap: 8px;
  }

  .camera-preview {
    width: 100%;
    height: 100%;
  }

  .take-photo-overlay {
    padding-bottom: 100px;
  }

  .countdown-circle {
    width: 160px;
    height: 160px;
    background: rgba(0, 0, 0, 0.4);
  }

  .countdown-number {
    font-size: 80px;
  }

  .camera-btn {
    width: 100%;
    min-width: auto;
    padding: 14px 20px;
  }

  /* 移动端预览样式 */
  .capture-preview {
    max-width: 100%;
  }

  .preview-image {
    width: 250px;
    height: 250px;
  }

  .preview-controls {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .preview-btn {
    width: 100%;
    min-width: auto;
    padding: 12px 20px;
  }
}
</style>